import { useStoredFlag } from '../../hooks/useStoredFlag';
import { useI18n } from '../../i18n/i18n-context';
import type { WritingAid } from './WritingAids';

// The writing aids, each on unless turned off: the grey rest of a word, the word list, and spell
// check.
const COMPLETE_WORDS_KEY = 'lyrmo.complete-words';
const WORD_LIST_KEY = 'lyrmo.word-list';
const SPELL_CHECK_KEY = 'lyrmo.spell-check';

/** Which writing aids are on. */
export interface WritingAidSettings {
  /** The rest of the word being typed, in grey after the caret. */
  readonly completeWords: boolean;
  /** The words that could be the one being typed, to pick from. */
  readonly showList: boolean;
  /** Words that look mistyped, underlined. */
  readonly spellCheck: boolean;
}

/** Which writing aids are on, remembered in this browser, and the switches that turn them on. */
export function useWritingAids(): {
  readonly settings: WritingAidSettings;
  readonly aids: readonly WritingAid[];
} {
  const { t } = useI18n();
  const [completeWords, setCompleteWords] = useStoredFlag(COMPLETE_WORDS_KEY, true);
  const [showList, setShowList] = useStoredFlag(WORD_LIST_KEY, true);
  const [spellCheck, setSpellCheck] = useStoredFlag(SPELL_CHECK_KEY, true);
  return {
    settings: { completeWords, showList, spellCheck },
    aids: [
      {
        label: t.editor.completeWords,
        hint: t.editor.completeWordsHint,
        on: completeWords,
        onChange: setCompleteWords,
      },
      {
        label: t.editor.wordList,
        hint: t.editor.wordListHint,
        on: showList,
        onChange: setShowList,
      },
      {
        label: t.editor.spellCheck,
        hint: t.editor.spellCheckHint,
        on: spellCheck,
        onChange: setSpellCheck,
      },
    ],
  };
}
